import Button from "@ui/button/Button"
import { useState } from "react"
import { toast } from "react-toastify"
import { systemErrorMessage, systemPost } from "src/services/superAdminApi"
import { Callout, Muted, Row, StatusBadge } from "./adminUi"
import { askValue } from "./confirm"
import { accessLocks, isBlocked } from "./userAccessLocks"

const NO_KYC_VERIFICATION = 2610
const NOT_STARTED_HINT = "User has not started KYC yet — they need to log in and open verification"

const text = (value: unknown) => typeof value === "string" ? value : undefined

/** Manual approval (Superadmin_Manual_Approval_FE.md): confirm e-mail, approve or revoke our KYC verdict. */
export const UserAccessActions = ({ token, flow, user, company, kyc, onChanged }: {
	token: string
	/** Newer overviews say which locks apply (Superadmin_User_KYC_FE.md §2); older ones fall back to accessLocks. */
	flow?: { email_required?: boolean; our_kyc_required?: boolean }
	user?: Record<string, unknown>
	company?: Record<string, unknown> | null
	kyc?: unknown
	onChanged: () => Promise<void> | void
}) => {
	const [busy, setBusy] = useState(false)
	const userUid = text(user?.uid)
	const guessed = accessLocks({ companyType: company?.type, companyRole: user?.company_role, registrationSource: company?.registration_source })
	const locks = { email: flow?.email_required ?? guessed.email, kyc: flow?.our_kyc_required ?? guessed.kyc }
	if (!userUid || (!locks.email && !locks.kyc)) return null

	const kycObject = kyc && typeof kyc === "object" ? kyc as { our_status?: unknown; verifications?: unknown } : {}
	const kycStatus = text(user?.kyc_status) ?? text(kycObject.our_status)
	const hasVerification = Array.isArray(kycObject.verifications) && kycObject.verifications.length > 0
	const emailPending = locks.email && user?.email_confirmed_at === null
	const kycApproved = kycStatus === "approved"
	const blocked = isBlocked(user?.status, user?.company_status)

	const run = async (action: () => Promise<boolean>) => {
		setBusy(true)
		try { if (await action()) await onChanged() } finally { setBusy(false) }
	}
	const confirmEmail = () => run(async () => {
		const response = await systemPost<{ already_confirmed?: boolean }>(token, "system/user/email/confirm", { user_uid: userUid })
		if (response.error) { toast.error("Confirm failed: " + systemErrorMessage(response.error)); return false }
		toast.success(response.data?.already_confirmed ? "Email was already confirmed" : "Email confirmed")
		return true
	})
	// The override response carries the raw KYC snapshot (`data`, personal data): it is never read, shown or stored.
	const override = (status: "approved" | "declined") => run(async () => {
		const approve = status === "approved"
		const reason = await askValue({
			title: approve ? "Approve KYC" : "Revoke KYC approval",
			body: approve ? "Overrides our KYC verdict for this user. The reason stays in the verification history." : "The user's cabinet will be closed again until KYC is approved.",
			confirmLabel: approve ? "Approve KYC" : "Revoke approval", danger: !approve,
			input: { label: "Reason", maxLength: 512 }
		})
		if (reason === null) return false
		const response = await systemPost(token, "system/kyc/verification/override", { user_uid: userUid, status, reason: reason.trim().slice(0, 512) })
		if (response.error) {
			toast.error(Number(response.error.code) === NO_KYC_VERIFICATION ? NOT_STARTED_HINT : "Override failed: " + systemErrorMessage(response.error))
			return false
		}
		toast.success(approve ? "KYC approved" : "KYC approval revoked")
		return true
	})

	return <section aria-label='Manual approval' style={{ display: "grid", gap: 10 }}>
		<strong>Manual approval</strong>
		{blocked && <Callout role='status' tone='warn'>This user is blocked (user: {text(user?.status) || "—"}, company: {text(user?.company_status) || "—"}). Approval does not unblock them.</Callout>}
		{locks.email && <Row style={{ gap: 12 }}>
			<span style={{ minWidth: 70 }}>E-mail</span><StatusBadge value={emailPending ? "unconfirmed" : user?.email_confirmed_at ? "confirmed" : "unknown"} />
			{emailPending && <Button variant='primarySmall' disabled={busy} onClick={() => void confirmEmail()}>Confirm email</Button>}
		</Row>}
		{locks.kyc && <Row style={{ gap: 12 }}>
			<span style={{ minWidth: 70 }}>Our KYC</span><StatusBadge value={kycStatus || "unknown"} />
			{!kycApproved && <Button variant='primarySmall' disabled={busy || !hasVerification} onClick={() => void override("approved")}>Approve KYC</Button>}
			{kycApproved && <Button variant='dangerSmall' disabled={busy} onClick={() => void override("declined")}>Revoke approval</Button>}
			{!kycApproved && !hasVerification && <Muted style={{ fontSize: 13 }}>{NOT_STARTED_HINT}</Muted>}
		</Row>}
		{emailPending && locks.kyc && !hasVerification && <Muted style={{ fontSize: 13 }}>Confirm the e-mail first: the user cannot log in, so they cannot start KYC.</Muted>}
	</section>
}
